import { FC, useCallback, useMemo, useRef } from "react";
import { CellClickedEvent, GridApi } from "ag-grid-community";
import { Box, Flex, Text } from "@radix-ui/themes";
import { IEntity, EntityFieldTree } from "@/types/breeze/Entity";
import { formatColumnName } from "@/utils/text";
import DataGrid, { DataGridColumn } from "@/components/ui/DataGrid";
import { formatDate } from "@/utils/date";
import {
  Field,
  FileFieldDisplayType,
  RelationshipFieldDisplayType,
  StandardFieldDisplayType,
  UserFieldDisplayType,
} from "@/types/breeze/Field";
import { PaginatedResult } from "@/types/breeze/Any";
import { IAny } from "@/types/breeze/Any";
import { FieldSelector } from "@/components/field-selector/FieldSelector";
import { ExternalLink } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { useEntities } from "@/hooks/useCommonQueries";

export interface EntityGridProps {
  entityDefinition: IEntity;
  entityData: PaginatedResult<IAny> | null;
  currentPage: number;
  currentPageSize: number;
  isLoading: boolean;
  selectedFields: string[];
  entityTree: EntityFieldTree[];
  rowSelection?: "single" | "multiple" | "none";
  disableFieldSelector?: boolean;
  enableInstantEdit?: boolean;
  selectedRecordIds?: (string | number)[];
  additionalActions?: (data: IAny) => React.ReactNode;
  linkTemplate?: (data: IAny) => string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onFilterChange: (query: string) => void;
  onSortChange?: (sortBy: string, sortDirection: string) => void;
  onSelectionChanged?: (selectedRows: IAny[]) => void;
  onFieldsChanged?: (fields: string[]) => void;
  onCellClicked?: (data: IAny) => void;
  onCellValueChanged?: (field: string, newValue: unknown, data: IAny) => void;
  onRelationshipClicked?: (
    fieldName: string,
    currentValue: unknown,
    targetEntity: IEntity | null,
    relationshipId: number,
    rowData: IAny,
  ) => void;
  onFileClicked?: (
    fieldName: string,
    currentValue: unknown,
    rowData: IAny,
  ) => void;
  onMultiSelectClicked?: (
    fieldName: string,
    currentValue: unknown,
    selectOptions: Array<{ value: string; label: string }>,
    rowData: IAny,
  ) => void;
  onRowView?: (data: IAny) => void;
}

const EntityGrid: FC<EntityGridProps> = ({
  entityDefinition,
  entityData,
  currentPage,
  currentPageSize,
  isLoading,
  selectedFields,
  entityTree,
  rowSelection,
  disableFieldSelector,
  enableInstantEdit = false,
  selectedRecordIds,
  additionalActions,
  linkTemplate,
  onPageChange,
  onPageSizeChange,
  onFilterChange,
  onSortChange,
  onSelectionChanged,
  onFieldsChanged,
  onCellClicked,
  onCellValueChanged,
  onRelationshipClicked,
  onFileClicked,
  onMultiSelectClicked,
  onRowView,
}) => {
  const { currentTenant } = useAuth();
  const { data: entities } = useEntities();
  const gridApiRef = useRef<GridApi | null>(null);

  const findFieldInTree = useCallback(
    (field: string): EntityFieldTree | undefined => {
      const parts = field.split(".");
      let node = entityTree.find((n: EntityFieldTree) => n.path === parts[0]);

      for (let i = 1; i < parts.length && node; i++) {
        node = node.children?.find((n: EntityFieldTree) => n.path === field);
      }

      return node;
    },
    [entityTree],
  );

  const getCellRenderer = useCallback(
    (fieldNode: EntityFieldTree | undefined, field: string) => {
      // Return a function that matches DataGridColumn's cellRenderer signature
      const CellRenderer = (data: IAny) => {
        // Handle nested field paths
        const fieldPath = field.split(".");
        let value: unknown = data;
        for (const path of fieldPath) {
          value = (value as Record<string, unknown>)?.[path];
          if (value === undefined) break;
        }

        const fieldDef = fieldNode?.definition;

        if (!fieldNode || !fieldDef) {
          return <DefaultCellRenderer value={value} field={fieldDef} />;
        }

        switch (fieldDef.display_type) {
          case StandardFieldDisplayType.CHECKBOX:
          case StandardFieldDisplayType.RADIO:
            return <BooleanCellRenderer value={value} field={fieldDef} />;
          case FileFieldDisplayType.FILE:
            return <FileCellRenderer value={value} field={fieldDef} />;
          case UserFieldDisplayType.USER:
            return <UserCellRenderer value={value} field={fieldDef} />;
          case StandardFieldDisplayType.JSON:
            return <JsonCellRenderer value={value} field={fieldDef} />;
          case StandardFieldDisplayType.TIMESTAMP:
            return <DateCellRenderer value={value} field={fieldDef} />;
          case RelationshipFieldDisplayType.RELATIONSHIP:
            return <RelationshipCellRenderer value={value} field={fieldDef} />;
          case "secret":
            return value ? (
              <span style={{ color: "var(--green-11)", fontSize: "12px" }}>
                ● Configured
              </span>
            ) : (
              <span style={{ color: "var(--gray-8)", fontSize: "12px" }}>
                Not set
              </span>
            );
          default:
            return <DefaultCellRenderer value={value} field={fieldDef} />;
        }
      };

      CellRenderer.displayName = `CellRenderer_${field}`;
      return CellRenderer;
    },
    [],
  );

  const buildHeaderName = useCallback(
    (field: string, fieldNode: EntityFieldTree | undefined): string => {
      const isNestedField = field.includes(".");

      if (isNestedField) {
        const parts = field.split(".");
        const headerParts: string[] = [];

        for (let i = 0; i < parts.length; i++) {
          const partialPath = parts.slice(0, i + 1).join(".");
          const node = findFieldInTree(partialPath);

          if (node?.definition?.label) {
            headerParts.push(node.definition.label);
          } else if (node?.name) {
            headerParts.push(formatColumnName(node.name));
          } else {
            headerParts.push(formatColumnName(parts[i]));
          }
        }

        if (headerParts.length > 3) {
          return `${headerParts[0]} › ... › ${
            headerParts[headerParts.length - 2]
          } › ${headerParts[headerParts.length - 1]}`;
        } else {
          return headerParts.join(" › ");
        }
      } else {
        return (
          fieldNode?.definition?.label ||
          formatColumnName(fieldNode?.name || field)
        );
      }
    },
    [findFieldInTree],
  );

  // Memoize the fields to show separately to minimize dependencies
  const fieldsToShow = useMemo(() => {
    if (selectedFields?.length === 0 || disableFieldSelector) {
      // For default fields, always use entity definition rather than dynamic data
      return entityDefinition.fields.map((f) => f.name);
    }
    return selectedFields?.filter((field) => field !== "__name") || [];
  }, [selectedFields, disableFieldSelector, entityDefinition.fields]);

  const columns: DataGridColumn<IAny>[] = useMemo(() => {
    if (!entityDefinition.fields.length || !entityTree) return [];

    const cols: DataGridColumn<IAny>[] = [];

    // Build columns for each field
    fieldsToShow.forEach((field) => {
      const fieldNode = findFieldInTree(field);
      const headerName = buildHeaderName(field, fieldNode);
      const isIdColumn = field.toLowerCase() === "id";

      const column: DataGridColumn<IAny> = {
        field,
        header: headerName,
        cellRenderer: getCellRenderer(fieldNode, field),
        width: isIdColumn ? 120 : Math.max(headerName.length * 8, 200),
        minWidth: isIdColumn ? 120 : 150,
        maxWidth: isIdColumn ? 150 : undefined,
        flex: isIdColumn ? 0 : 1,
        fieldType: fieldNode?.definition
          ? {
              database_type: fieldNode.definition.database_type,
              display_type: fieldNode.definition.display_type,
              create_db_column: fieldNode.definition.create_db_column,
              ...(fieldNode.definition.display_type ===
                RelationshipFieldDisplayType.RELATIONSHIP &&
              fieldNode.definition.relationship_id
                ? {
                    relationshipMetadata: (() => {
                      const relationship =
                        entityDefinition.source_relationships.find(
                          (r) => r.id === fieldNode.definition.relationship_id,
                        );
                      const targetEntity =
                        entities?.find(
                          (e) => e.id === relationship?.target_entity_id,
                        ) || null;
                      const targetNameField = targetEntity?.fields?.find(
                        (f) => f.is_name_field,
                      )?.name;
                      return {
                        relationshipId: fieldNode.definition.relationship_id,
                        targetEntity,
                        targetNameField,
                      };
                    })(),
                  }
                : {}),
              ...((fieldNode.definition.display_type ===
                StandardFieldDisplayType.SELECT ||
                fieldNode.definition.display_type ===
                  StandardFieldDisplayType.RADIO) &&
              fieldNode.definition.options?.select?.options
                ? {
                    selectOptions: fieldNode.definition.options.select.options,
                    isMultiSelect:
                      fieldNode.definition.options.select.multiple || false,
                  }
                : {}),
            }
          : undefined,
      };

      cols.push(column);
    });

    return cols;
  }, [
    entityDefinition.fields,
    entityDefinition.source_relationships,
    entityTree,
    fieldsToShow,
    entities,
    findFieldInTree,
    buildHeaderName,
    getCellRenderer,
  ]);

  const handleCellClicked = useCallback(
    (_: CellClickedEvent, data: IAny) => {
      if (onCellClicked) {
        onCellClicked(data);
      }
    },
    [onCellClicked],
  );

  const handleCellValueChanged = useCallback(
    (event: unknown, data: IAny) => {
      const cellEvent = event as {
        colDef: { field: string };
        oldValue: unknown;
        newValue: unknown;
      };

      if (onCellValueChanged) {
        onCellValueChanged(cellEvent.colDef.field, cellEvent.newValue, data);
      }
    },
    [onCellValueChanged],
  );

  const actions = useMemo(() => {
    const ActionsCellRenderer = (data: IAny) => {
      if (onRowView) {
        return (
          <Flex
            gap="3"
            direction="row"
            align="center"
            style={{ width: "80px" }}
          >
            <span
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRowView(data);
              }}
            >
              <ExternalLink size={16} />
            </span>
            {additionalActions?.(data)}
          </Flex>
        );
      }

      const linkUrl = linkTemplate
        ? linkTemplate(data)
        : `/org/${currentTenant?.id}/entities/${entityDefinition.name}/${data.id}`;

      const handleOpenInNewTab = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent ag-grid from swallowing the click
        
        console.log("[EntityGrid] Open in new tab clicked! Target:", linkUrl);

        if (window.parent !== window) {
          console.log("[EntityGrid] In iframe, posting message to parent...");
          window.parent.postMessage(
            {
              type: "open-in-new-tab",
              path: linkUrl,
            },
            "*"
          );
        } else {
          console.log("[EntityGrid] Standalone mode, opening window directly...");
          window.open(linkUrl, "_blank");
        }
      };

      return (
        <Flex gap="3" direction="row" align="center" style={{ width: "80px" }}>
          <button
            type="button"
            style={{ 
              cursor: "pointer", 
              color: "var(--accent-11)", 
              background: "none", 
              border: "none", 
              padding: 0,
              display: "flex",
              alignItems: "center"
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => {
              // Using onMouseUp instead of onClick because ag-grid swallows clicks
              handleOpenInNewTab(e);
            }}
            title="Open in new tab"
          >
            <ExternalLink size={16} />
          </button>
          {additionalActions?.(data)}
        </Flex>
      );
    };

    return ActionsCellRenderer;
  }, [
    currentTenant,
    entityDefinition,
    linkTemplate,
    additionalActions,
    onRowView,
  ]);

  const topContent = useMemo(() => {
    if (disableFieldSelector || !onFieldsChanged) return undefined;

    return (
      <Box flexShrink="0">
        <FieldSelector
          value={selectedFields}
          onChange={onFieldsChanged}
          treeData={entityTree}
          placeholder="Select Fields"
        />
      </Box>
    );
  }, [disableFieldSelector, onFieldsChanged, selectedFields, entityTree]);

  const onGridReady = useCallback((params: { api: GridApi }) => {
    gridApiRef.current = params.api;
  }, []);

  return (
    <DataGrid<IAny>
      data={entityData}
      columns={columns}
      storageKey={`entity_${entityDefinition.id}`}
      isLoading={isLoading}
      currentPage={currentPage}
      currentPageSize={currentPageSize}
      rowSelection={rowSelection}
      topContent={topContent}
      actions={actions}
      actionsWidth={80}
      enableInstantEdit={enableInstantEdit}
      selectedRowIds={selectedRecordIds}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onFilterChange={onFilterChange}
      onSortChange={onSortChange}
      onSelectionChanged={onSelectionChanged}
      onCellClicked={handleCellClicked}
      onGridReady={onGridReady}
      onCellValueChanged={handleCellValueChanged}
      onRelationshipEdit={onRelationshipClicked}
      onFileEdit={onFileClicked}
      onMultiSelectEdit={onMultiSelectClicked}
    />
  );
};

// Cell renderer components
const DefaultCellRenderer = ({
  value,
  field,
}: {
  value: unknown;
  field?: Field;
}) => {
  const displayValue = field?.options?.input?.masked
    ? "********"
    : String(value ?? "");
  return (
    <Text
      size="2"
      style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
    >
      {displayValue}
    </Text>
  );
};

const RelationshipCellRenderer = ({
  value,
}: {
  value: unknown;
  field?: Field;
}) => {
  if (value === null || value === undefined) {
    return (
      <Text
        size="2"
        color="gray"
        style={{
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        -
      </Text>
    );
  }

  if (Array.isArray(value)) {
    return (
      <Text
        size="2"
        style={{
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value.length} item{value.length === 1 ? "" : "s"}
      </Text>
    );
  }

  if (typeof value === "object" && value !== null) {
    const valueObj = value as Record<string, unknown>;

    // Check for __count first (for collection relationships)
    if (Object.keys(valueObj).includes("__count")) {
      return (
        <Text
          size="2"
          style={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {String(valueObj.__count)} item{valueObj.__count === 1 ? "" : "s"}
        </Text>
      );
    }

    // Check for __name (for single relationships)
    if (Object.keys(valueObj).includes("__name")) {
      return (
        <Text
          size="2"
          style={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {String(valueObj.__name)}
        </Text>
      );
    }

    // Check for common name fields
    const nameFields = ["name", "title", "description", "label"];
    for (const field of nameFields) {
      if (valueObj[field] && typeof valueObj[field] === "string") {
        return (
          <Text
            size="2"
            style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {String(valueObj[field])}
          </Text>
        );
      }
    }

    // If it has an id, show it with a label
    if (valueObj.id) {
      return (
        <Text
          size="2"
          style={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          ID: {String(valueObj.id)}
        </Text>
      );
    }
  }

  return (
    <Text
      size="2"
      style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
    >
      {String(value)}
    </Text>
  );
};

const UserCellRenderer = ({ value }: { value: unknown; field?: Field }) => {
  if (!value || typeof value !== "object") {
    return (
      <Text
        size="2"
        color="gray"
        style={{
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        None
      </Text>
    );
  }

  const userValue = value as Record<string, unknown>;
  if (!userValue.first_name && !userValue.last_name) {
    return (
      <Text
        size="2"
        color="gray"
        style={{
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        None
      </Text>
    );
  }

  const name = (userValue.first_name || "") + " " + (userValue.last_name || "");

  return (
    <Text
      size="2"
      style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
    >
      {name.trim()}
    </Text>
  );
};

const BooleanCellRenderer = ({ value }: { value: unknown; field?: Field }) => {
  return (
    <Text
      size="2"
      style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
      title={String(!!value)}
    >
      {value ? "Yes" : "No"}
    </Text>
  );
};

const JsonCellRenderer = ({ value }: { value: unknown; field?: Field }) => {
  const json = value || {};
  return (
    <Text
      size="2"
      style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
      title={JSON.stringify(json)}
    >
      {JSON.stringify(json)}
    </Text>
  );
};

const FileCellRenderer = ({ value }: { value: unknown; field?: Field }) => {
  const fileData =
    value && typeof value === "object" && value !== null
      ? (value as Record<string, unknown>).__data
      : undefined;
  const files = Array.isArray(fileData) ? fileData : [];
  return (
    <Text
      size="2"
      style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
    >
      {files.length} file{files.length === 1 ? "" : "s"}
    </Text>
  );
};

const DateCellRenderer = ({ value }: { value: unknown; field?: Field }) => {
  if (!value) return null;
  const dateValue =
    typeof value === "string" || value instanceof Date ? value : String(value);
  const formattedDate = formatDate(dateValue);
  return (
    <Text
      size="2"
      style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
      title={formattedDate}
    >
      {formattedDate}
    </Text>
  );
};

export {
  UserCellRenderer,
  BooleanCellRenderer,
  JsonCellRenderer,
  FileCellRenderer,
  DateCellRenderer,
  RelationshipCellRenderer,
};

export default EntityGrid;
